/**
 * Listing Comparisons utilities.
 *
 * Stores comparison set in localStorage: up to MAX_COMPARE listings.
 * Key: 'compareListings' → JSON array of { id, title, price, image }
 */

const STORAGE_KEY = 'compareListings';
export const MAX_COMPARE = 3;

/**
 * Get current comparison set from localStorage.
 * @returns {Object[]}
 */
export const getCompareSet = () => {
  if (typeof localStorage === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch (_) {
    return [];
  }
};

/**
 * Add a listing to the comparison set.
 * @param {{ id, title, price, image }} listing
 * @returns {{ added: boolean, set: Object[], full: boolean }}
 */
export const addToCompare = (listing) => {
  const current = getCompareSet();
  if (current.find(l => l.id === listing.id)) {
    return { added: false, set: current, full: current.length >= MAX_COMPARE };
  }
  if (current.length >= MAX_COMPARE) {
    return { added: false, set: current, full: true };
  }
  const updated = [...current, listing];
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }
  return { added: true, set: updated, full: updated.length >= MAX_COMPARE };
};

/**
 * Remove a listing from the comparison set.
 * @param {string} listingId
 * @returns {Object[]}
 */
export const removeFromCompare = (listingId) => {
  const updated = getCompareSet().filter(l => l.id !== listingId);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }
  return updated;
};

/**
 * Clear the entire comparison set.
 */
export const clearCompare = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
};

/**
 * Check if a listing is in the comparison set.
 * @param {string} listingId
 * @returns {boolean}
 */
export const isInCompare = (listingId) =>
  getCompareSet().some(l => l.id === listingId);
